import React, { useState, useEffect } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes, FaArrowUp, FaArrowDown } from "react-icons/fa";
import "../../styles/circuitoDesignSystem.css";

const TIPOS = [
  { value: "vaqueiro", label: "Vaqueiro" },
  { value: "esteireiro", label: "Esteireiro" },
  { value: "cavaloPuxada", label: "Cavalo de Puxada" },
  { value: "cavaloEsteira", label: "Cavalo de Esteira" }
];

export default function CircuitoRankingPainel() {
  const { circuito, buscarCircuito } = useOutletContext();
  const [participantes, setParticipantes] = useState([]);
  
  const [modalAberto, setModalAberto] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [msgSinc, setMsgSinc] = useState(null);
  const [reordenando, setReordenando] = useState(false);

  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("vaqueiro");

  const defaultForm = {
    _id: null,
    nome: "",
    tipo: "vaqueiro",
    categoria: circuito?.categorias?.[0]?.nome || circuito?.categorias?.[0] || "",
    historico: []
  };

  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (circuito) {
      setParticipantes(circuito.participantes || []);
      if (!filtroCategoria && circuito.categorias?.length > 0) {
        setFiltroCategoria(circuito.categorias[0].nome || circuito.categorias[0]);
      }
    }
  }, [circuito]);

  // 💡 NOVO: Sincronizar inscrições pagas → ranking
  const handleSincronizar = async () => {
    if (!window.confirm("Isso vai adicionar ao ranking todos os vaqueiros e cavalos com inscrição PAGA que ainda não estão cadastrados. Continuar?")) return;
    setSincronizando(true);
    setMsgSinc(null);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.post(
        `https://api.ticketdovaqueiro.com/api/circuitos/${circuito._id}/sincronizar-inscricoes`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsgSinc({ tipo: "sucesso", texto: resp.data.mensagem });
      buscarCircuito();
    } catch (error) {
      setMsgSinc({ tipo: "erro", texto: error.response?.data?.erro || "Erro ao sincronizar." });
    } finally {
      setSincronizando(false);
    }
  };

  const handleNovo = () => {
    setModoEdicao(false);
    setForm(defaultForm);
    setModalAberto(true);
  };

  const handleEditar = (p) => {
    setModoEdicao(true);
    setForm({
      ...p,
      historico: p.historico || []
    });
    setModalAberto(true);
  };

  // 💡 NOVO: Função para excluir participante inteiro
  const handleDelete = async (idParticipante) => {
    if (!window.confirm("Deseja realmente EXCLUIR este participante e todo o seu histórico do ranking?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`https://api.ticketdovaqueiro.com/api/circuitos/${circuito._id}/participante/${idParticipante}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      alert("Participante excluído com sucesso!");
      buscarCircuito(); 
    } catch (error) {
      alert("Erro ao excluir participante.");
    }
  };

  const addEtapa = () => {
    setForm({
      ...form,
      historico: [
        { nomeEtapa: "", dataEtapa: "", resultados: [{ posicao: "", pontos: 0 }] },
        ...form.historico
      ]
    });
  };

  const addResultado = (etapaIndex) => {
    const novoHistorico = [...form.historico];
    novoHistorico[etapaIndex].resultados.push({ posicao: "", pontos: 0 });
    setForm({ ...form, historico: novoHistorico });
  };

  // Atalhos rápidos: adicionam uma classificação com pontuação predefinida
  // sem substituir as classificações já cadastradas na etapa.
  const addResultadoRapido = (etapaIndex, posicao) => {
    setForm(anterior => ({
      ...anterior,
      historico: anterior.historico.map((etapa, index) =>
        index === etapaIndex
          ? { ...etapa, resultados: [...(etapa.resultados || []), { posicao, pontos: 5 }] }
          : etapa
      )
    }));
  };

  const updateEtapa = (index, campo, valor) => {
    const novoHistorico = [...form.historico];
    novoHistorico[index][campo] = valor;
    setForm({ ...form, historico: novoHistorico });
  };

  const updateResultado = (etapaIndex, resIndex, campo, valor) => {
    const novoHistorico = [...form.historico];
    novoHistorico[etapaIndex].resultados[resIndex][campo] = campo === "pontos" ? Number(valor) : valor;
    setForm({ ...form, historico: novoHistorico });
  };

  const removeEtapa = (index) => {
    const novoHistorico = form.historico.filter((_, i) => i !== index);
    setForm({ ...form, historico: novoHistorico });
  };

  const removeResultado = (etapaIndex, resIndex) => {
    const novoHistorico = [...form.historico];
    novoHistorico[etapaIndex].resultados = novoHistorico[etapaIndex].resultados.filter((_, i) => i !== resIndex);
    setForm({ ...form, historico: novoHistorico });
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const totalPontos = (form.historico || []).reduce(
        (total, etapa) => total + (etapa.resultados || []).reduce(
          (subtotal, resultado) => subtotal + (Number(resultado.pontos) || 0), 0
        ), 0
      );

      const payload = {
        ...form,
        pontos: totalPontos,
        etapas: (form.historico || []).length,
        ordemManual: form.ordemManual ?? 0
      };

      // Apenas falhas na gravação devem acionar o alerta de erro.
      // A atualização visual não deve transformar um salvamento bem-sucedido em erro.
      let resposta;
      if (modoEdicao) {
        resposta = await axios.put(
          `https://api.ticketdovaqueiro.com/api/circuitos/${circuito._id}/update-participante`,
          { ...payload, idParticipante: form._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        resposta = await axios.put(
          `https://api.ticketdovaqueiro.com/api/circuitos/${circuito._id}/add-participante`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (modoEdicao) {
        // Reflete a edição imediatamente, sem precisar atualizar a página.
        // Preserva campos do participante que não foram alterados no formulário.
        setParticipantes(anteriores => anteriores.map(participante =>
          String(participante._id) === String(form._id)
            ? { ...participante, ...payload }
            : participante
        ));
      } else {
        // Em cadastros, aproveita o participante retornado pela API quando disponível.
        const dados = resposta?.data;
        const criado = dados?.participante || dados?.novoParticipante ||
          (dados?._id ? dados : null);
        if (criado?._id) {
          setParticipantes(anteriores => [...anteriores, { ...payload, ...criado }]);
        } else {
          // Se a API não devolveu o ID do novo participante, recarrega a listagem.
          try {
            await buscarCircuito();
          } catch (erroAtualizacao) {
            console.warn("Participante salvo, mas não foi possível recarregar o ranking:", erroAtualizacao);
          }
        }
      }

      setModalAberto(false);
      alert("Ranking salvo com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar participante do ranking:", error);
      alert(error.response?.data?.erro || "Erro ao salvar participante.");
    } finally {
      setLoading(false);
    }
  };

  const listaFiltrada = participantes
    .filter(p => p.categoria === filtroCategoria && p.tipo === filtroTipo)
    .sort((a, b) => {
      if (b.pontos !== a.pontos) return b.pontos - a.pontos;       // 1º critério: pontos
      const oa = a.ordemManual ?? 0, ob = b.ordemManual ?? 0;
      if (oa !== ob) return oa - ob;                                // 2º critério: ordem manual
      return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
    });

  // Posição com empate (standard competition ranking: 1, 2, 2, 4)
  const posicoes = listaFiltrada.map((p, idx, arr) => {
    if (idx > 0 && arr[idx - 1].pontos === p.pontos) {
      // mesmo número do anterior (empate)
      return null; // marcado depois
    }
    return idx + 1;
  });
  // Preenche os nulls com a posição do empate
  let ultimaPos = 1;
  const posicaoFinal = posicoes.map((pos, idx) => {
    if (pos !== null) { ultimaPos = pos; return pos; }
    return ultimaPos;
  });

  // Verifica se um participante está empatado com vizinho (mesma pontuação)
  const estaEmpatado = (idx) => {
    const atual = listaFiltrada[idx];
    const anterior = listaFiltrada[idx - 1];
    const proximo = listaFiltrada[idx + 1];
    return (anterior && anterior.pontos === atual.pontos) ||
           (proximo && proximo.pontos === atual.pontos);
  };

  // Move um participante para cima/baixo na lista e persiste a ordem
  const moverParticipante = async (idx, direcao) => {
    const novoIdx = idx + direcao;
    if (novoIdx < 0 || novoIdx >= listaFiltrada.length) return;

    // Só permite trocar entre quem tem a MESMA pontuação (empate)
    if (listaFiltrada[idx].pontos !== listaFiltrada[novoIdx].pontos) return;

    const novaLista = [...listaFiltrada];
    [novaLista[idx], novaLista[novoIdx]] = [novaLista[novoIdx], novaLista[idx]];

    setReordenando(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://api.ticketdovaqueiro.com/api/circuitos/${circuito._id}/reordenar-participantes`,
        { ordem: novaLista.map(p => p._id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Atualiza local imediatamente (otimista) refletindo ordemManual
      buscarCircuito();
    } catch (error) {
      alert("Erro ao reordenar.");
    } finally {
      setReordenando(false);
    }
  };

  return (
    <div style={{ paddingBottom: "40px" }}>
      <div className="pc-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 className="pc-page-header__title">Gerenciar Ranking</h1>
          <p className="pc-page-header__subtitle">Adicione as pontuações e histórico de etapas.</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handleSincronizar}
            disabled={sincronizando}
            className="c-btn c-btn--outline"
            style={{ display: "flex", gap: 8, alignItems: "center", padding: "0 20px", height: 44, borderColor: "#22c55e", color: "#22c55e" }}
            title="Importa automaticamente todos os vaqueiros e cavalos com inscrição paga"
          >
            {sincronizando ? "Sincronizando..." : "⚡ Sincronizar Inscrições"}
          </button>
          <button onClick={handleNovo} className="c-btn c-btn--primary" style={{ display: "flex", gap: 8, alignItems: "center", padding: "0 20px", height: 44 }}>
            <FaPlus /> Novo Participante
          </button>
        </div>
      </div>

      {/* Feedback da sincronização */}
      {msgSinc && (
        <div style={{
          marginBottom: 16,
          padding: "12px 16px",
          borderRadius: 8,
          background: msgSinc.tipo === "sucesso" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
          border: `1px solid ${msgSinc.tipo === "sucesso" ? "#22c55e" : "#ef4444"}`,
          color: msgSinc.tipo === "sucesso" ? "#22c55e" : "#ef4444",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 14,
        }}>
          <span>{msgSinc.texto}</span>
          <button onClick={() => setMsgSinc(null)} style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
      )}

      <div className="pc-card" style={{ marginBottom: 24, display: "flex", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Categoria</label>
          <select className="c-input" style={{ width: "100%" }} value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}>
             {circuito?.categorias?.map((c, i) => {
                const catName = c.nome || c;
                return <option key={i} value={catName}>{catName}</option>
             })}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Tipo</label>
          <select className="c-input" style={{ width: "100%" }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
             {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div className="pc-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "10px 16px", fontSize: 12, color: "rgba(255,255,255,0.5)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          Participantes com a mesma pontuação ficam <strong style={{ color: "var(--c-accent)" }}>empatados (=)</strong> na mesma posição. Use as setas ↑↓ para definir qual aparece primeiro entre os empatados.
        </div>
        <table className="pc-table">
          <thead>
            <tr>
              <th style={{ width: "80px", textAlign: "center" }}>Posição</th>
              <th>Nome</th>
              <th style={{ textAlign: "center" }}>Etapas Corridas</th>
              <th style={{ textAlign: "center" }}>Total de Pontos</th>
              <th style={{ textAlign: "center" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {listaFiltrada.length === 0 ? (
               <tr><td colSpan="5" style={{ textAlign: "center", padding: 30, color: "#666" }}>Nenhum participante aqui.</td></tr>
            ) : (
               listaFiltrada.map((p, idx) => {
                 const empatado = estaEmpatado(idx);
                 const podeSubir = idx > 0 && listaFiltrada[idx - 1].pontos === p.pontos;
                 const podeDescer = idx < listaFiltrada.length - 1 && listaFiltrada[idx + 1].pontos === p.pontos;
                 return (
                 <tr key={p._id}>
                   <td style={{ textAlign: "center", fontWeight: "bold", fontSize: 16 }}>
                     {posicaoFinal[idx]}º
                     {empatado && <span title="Empate" style={{ marginLeft: 4, fontSize: 11, color: "var(--c-accent)" }}>=</span>}
                   </td>
                   <td style={{ fontWeight: "bold", color: "#fff" }}>{p.nome}</td>
                   <td style={{ textAlign: "center" }}>{p.etapas}</td>
                   <td style={{ textAlign: "center", color: "var(--c-accent)", fontWeight: "bold", fontSize: 16 }}>{p.pontos} pts</td>
                   <td style={{ textAlign: "center" }}>
                     <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
                       {/* Setas de reordenação — só aparecem quando há empate */}
                       {(podeSubir || podeDescer) && (
                         <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                           <button
                             onClick={() => moverParticipante(idx, -1)}
                             disabled={!podeSubir || reordenando}
                             title="Subir"
                             style={{ background: podeSubir ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.03)", color: podeSubir ? "#fff" : "#555", border: "none", padding: "2px 6px", borderRadius: 4, cursor: podeSubir ? "pointer" : "default", fontSize: 10 }}
                           ><FaArrowUp /></button>
                           <button
                             onClick={() => moverParticipante(idx, 1)}
                             disabled={!podeDescer || reordenando}
                             title="Descer"
                             style={{ background: podeDescer ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.03)", color: podeDescer ? "#fff" : "#555", border: "none", padding: "2px 6px", borderRadius: 4, cursor: podeDescer ? "pointer" : "default", fontSize: 10 }}
                           ><FaArrowDown /></button>
                         </div>
                       )}
                       <button onClick={() => handleEditar(p)} style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}><FaEdit /> Editar</button>
                       <button onClick={() => handleDelete(p._id)} style={{ background: "rgba(239, 68, 68, 0.15)", color: "#ef4444", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer" }} title="Excluir"><FaTrash /></button>
                     </div>
                   </td>
                 </tr>
                 );
               })
            )}
          </tbody>
        </table>
      </div>

      {modalAberto && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.8)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999 }}>
          <div style={{ background: "#171717", width: "90%", maxWidth: 600, maxHeight: "90vh", borderRadius: 16, display: "flex", flexDirection: "column" }}>
            
            <div style={{ padding: 24, borderBottom: "1px solid rgba(255,255,255,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
               <h3 style={{ margin: 0, color: "#fff", fontSize: 20 }}>{modoEdicao ? "Editar Participante e Histórico" : "Cadastrar Participante"}</h3>
               <button onClick={() => setModalAberto(false)} style={{ background: "transparent", border: "none", color: "#888", cursor: "pointer", fontSize: 20 }}><FaTimes /></button>
            </div>

            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
               <form id="rankingForm" onSubmit={handleSalvar}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                     <div style={{ gridColumn: "1 / -1" }}>
                       <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#aaa" }}>Nome do Competidor/Cavalo *</label>
                       <input required type="text" className="c-input" style={{ width: "100%" }} value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} />
                     </div>
                     <div>
                       <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#aaa" }}>Tipo *</label>
                       <select required className="c-input" style={{ width: "100%" }} value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                          {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                       </select>
                     </div>
                     <div>
                       <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#aaa" }}>Categoria *</label>
                       <select required className="c-input" style={{ width: "100%" }} value={form.categoria} onChange={e => setForm({...form, categoria: e.target.value})}>
                          {circuito?.categorias?.map((c, i) => <option key={i} value={c.nome || c}>{c.nome || c}</option>)}
                       </select>
                     </div>
                  </div>

                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 24 }}>
                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <h4 style={{ margin: 0, color: "#fff" }}>Histórico de Etapas</h4>
                        <button type="button" onClick={addEtapa} style={{ background: "var(--c-accent)", color: "#000", border: "none", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: "bold", display: "flex", alignItems: "center", gap: 6 }}><FaPlus /> Cadastrar Etapa</button>
                     </div>

                     {form.historico.length === 0 ? (
                        <p style={{ color: "#666", fontSize: 14 }}>Nenhuma etapa cadastrada ainda.</p>
                     ) : (
                        form.historico.map((etapa, eIdx) => (
                          <div key={eIdx} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                             <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                                <input required type="text" placeholder="Nome da Etapa (Ex: 3ª Vaquejada PixBet)" className="c-input" style={{ flex: 2 }} value={etapa.nomeEtapa} onChange={e => updateEtapa(eIdx, "nomeEtapa", e.target.value)} />
                                <input type="text" placeholder="Data (Opcional)" className="c-input" style={{ flex: 1 }} value={etapa.dataEtapa} onChange={e => updateEtapa(eIdx, "dataEtapa", e.target.value)} />
                                <button type="button" onClick={() => removeEtapa(eIdx)} style={{ background: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "none", padding: "0 12px", borderRadius: 8, cursor: "pointer" }}><FaTrash /></button>
                             </div>

                             <div style={{ paddingLeft: 16, borderLeft: "2px solid rgba(255,255,255,0.1)" }}>
                                <label style={{ display: "block", fontSize: 12, color: "#888", marginBottom: 8, marginTop: 12 }}>Classificações ganhas nesta etapa:</label>
                                {etapa.resultados.map((res, rIdx) => (
                                   <div key={rIdx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                                      <input required type="text" placeholder="Posição (Ex: 1º Lugar, Bonificação)" className="c-input" style={{ flex: 2, height: 36, fontSize: 13 }} value={res.posicao} onChange={e => updateResultado(eIdx, rIdx, "posicao", e.target.value)} />
                                      <input required type="number" placeholder="Pontos ganhos" className="c-input" style={{ width: 120, height: 36, fontSize: 13 }} value={res.pontos} onChange={e => updateResultado(eIdx, rIdx, "pontos", e.target.value)} />
                                      {etapa.resultados.length > 1 && (
                                        <button type="button" onClick={() => removeResultado(eIdx, rIdx)} style={{ background: "transparent", color: "#ef4444", border: "none", cursor: "pointer", padding: 8 }}><FaTimes /></button>
                                      )}
                                   </div>
                                ))}
                                <button type="button" onClick={() => addResultado(eIdx)} style={{ background: "transparent", color: "var(--c-accent)", border: "1px dashed var(--c-accent)", padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11, marginTop: 4 }}>+ Adicionar outra posição</button>
                                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 12 }}>
                                  <span style={{ fontSize: 11, color: "#999", marginRight: 2 }}>Adicionar rápido:</span>
                                  <button type="button" onClick={() => addResultadoRapido(eIdx, "senha antecipada")} style={{ background: "rgba(245, 194, 66, 0.1)", color: "var(--c-accent)", border: "1px solid rgba(245, 194, 66, 0.35)", padding: "7px 10px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>+ Senha antecipada · 5 pts</button>
                                  <button type="button" onClick={() => addResultadoRapido(eIdx, "senha batida")} style={{ background: "rgba(245, 194, 66, 0.1)", color: "var(--c-accent)", border: "1px solid rgba(245, 194, 66, 0.35)", padding: "7px 10px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>+ Senha batida · 5 pts</button>
                                </div>
                             </div>
                          </div>
                        ))
                     )}
                  </div>
               </form>
            </div>

            <div style={{ padding: 24, borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", gap: 12 }}>
               <button type="button" onClick={() => setModalAberto(false)} className="c-btn c-btn--outline" style={{ flex: 1, height: 48 }}>Cancelar</button>
               <button type="submit" form="rankingForm" disabled={loading} className="c-btn c-btn--primary" style={{ flex: 2, height: 48, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                 <FaSave /> {loading ? "Salvando..." : "Salvar Participante e Histórico"}
               </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}